import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

// The counselor-label column width shared by the Mitarbeiterübersicht and Verteilung AW/BG tables —
// the two tables whose sticky first column is a Mitarbeiter:in rather than a date. Wide enough that a
// "Nachname, Vorname (Kürzel)" label wraps to at most two lines instead of three or four.
export const COUNSELOR_COLUMN_WIDTH = 300;

// Same tinted header on every Dienstplan table: Daten erfassen, Mitarbeiterübersicht, Verteilung AW/BG.
export const HEADER_BACKGROUND = "fsw.blue-5";
// A deeper tint than the header so a totals/Summe row stands out as the table's summary line.
const TOTALS_BACKGROUND = "fsw.blue-10";

// MUI only defaults `scope` on head-variant cells, so a body row header has to set it itself — and
// jsx-a11y/scope cannot see through MUI's `component` polymorphism to tell it renders a <th>.
export const ROW_HEADER_PROPS = { component: "th", scope: "row" } as const;

/**
 * What a cell *means*, not how it looks — every Dienstplan table hands this to
 * {@link DutyScheduleTableLayout} instead of raw sx, so the actual colors/weights live in one place:
 * - `muted` — a summary row that isn't the grand total (Mitarbeiterübersicht's "nicht besetzt" row,
 *   and its Summe column on every ordinary row).
 * - `mutedItalic` — same, but for the sticky Mitarbeiter:in cell of that row specifically — the
 *   only cell of an unfilled row that reads in italics.
 * - `totals` — the grand-total row/column.
 * - `match` — Daten erfassen's counselor-filter highlight.
 * - `inactive` — Daten erfassen's "no Dienst here" cell.
 */
export type DutyScheduleCellHighlight = "none" | "muted" | "mutedItalic" | "totals" | "match" | "inactive";

export const useDutyScheduleTableStyles = () => ({
  tableContainer: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
  } satisfies SystemStyleObject<Theme>,
  // Pinning the table to the sum of its column widths is what makes those widths hold: left at the
  // default 100% the fixed layout would hand the leftover space back to the columns and stretch them.
  table: (width: number) =>
    ({
      tableLayout: "fixed",
      width,
      minWidth: width,
    }) satisfies SystemStyleObject<Theme>,
  headerCell: {
    bgcolor: HEADER_BACKGROUND,
    color: "text.secondary",
    fontWeight: 700,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  } satisfies SystemStyleObject<Theme>,
  // The sticky first column of the header row (Datum / Mitarbeiter:in) — one z-index above the
  // sticky body cells below so the header stays on top while both scroll under it horizontally.
  headerRowHeaderCell: (width: number) =>
    ({
      position: "sticky",
      left: 0,
      zIndex: 3,
      width,
    }) satisfies SystemStyleObject<Theme>,
  // The sticky first cell of every body row. Its highlight (if any) is layered on top via
  // `cellHighlight`, same as any other column.
  rowHeaderCell: (width: number) =>
    ({
      position: "sticky",
      left: 0,
      zIndex: 2,
      width,
      bgcolor: HEADER_BACKGROUND,
      // Wraps rather than truncating — the counselor name must stay fully visible, not hidden behind
      // an ellipsis, so a long one grows the row instead of the column.
      whiteSpace: "normal",
      wordBreak: "break-word",
    }) satisfies SystemStyleObject<Theme>,
  // The border/padding a Mitarbeiterübersicht/Verteilung AW/BG numeric column shares — Daten
  // erfassen's date grid doesn't use it.
  borderedCell: {
    px: 1,
    borderLeft: 1,
    borderColor: "divider",
  } satisfies SystemStyleObject<Theme>,
  // A column whose body cells are always bold (a sticky column's label, or a Summe column) —
  // header cells are already bold via `headerCell`, so this only ever applies to the body.
  boldCell: {
    fontWeight: 700,
  } satisfies SystemStyleObject<Theme>,
  // The one place a DutyScheduleCellHighlight becomes an actual look.
  cellHighlight: (highlight: DutyScheduleCellHighlight = "none"): SystemStyleObject<Theme> => {
    switch (highlight) {
      case "totals":
        return { bgcolor: TOTALS_BACKGROUND, fontWeight: 700 };
      case "muted":
        return { bgcolor: HEADER_BACKGROUND };
      case "mutedItalic":
        return { bgcolor: HEADER_BACKGROUND, fontStyle: "italic" };
      case "match":
        // A non-colour cue alongside the tint, so the filter highlight does not rely on colour alone.
        return {
          bgcolor: (theme: Theme) => alpha(theme.palette.fsw.yellow, 0.25),
          fontWeight: 700,
          border: 1,
          borderColor: (theme: Theme) => theme.palette.fsw.yellow,
        };
      case "inactive":
        return { bgcolor: "action.hover" };
      case "none":
        return {};
    }
  },
});
